import { el, clear } from '../../core/dom.js';
import { api } from '../../core/api.js';
import { navigate } from '../../core/router.js';
import { withProduct } from '../../core/route-map.js';
import { toast } from '../../components/toast.js';
import { skeleton } from '../../components/skeleton.js';
import { errorPanel } from '../../components/error.js';
import { button } from '../../components/button.js';
import { groupByDay } from './when.js';
import { disposeBlockCharts } from '../../components/blocks/index.js';
import { isBonnie, renderMessage } from './messages.js';
import { mountComposer } from './composer.js';
import { mountMembers } from './members.js';
import { subscribe } from './socket.js';
import { uploadFiles } from './upload.js';
import { archiveChat, exportMessage } from './actions.js';

/**
 * @param {{ groupId: string, me: object, productId: string, productName: string, chats: object[] }} ctx
 */
export function renderThread(ctx) {
  const groupId = ctx.groupId;
  const known = (ctx.chats || []).find((chat) => chat.id === groupId);
  const title = el('h1', { class: 'thread-title' }, (known && known.name) || 'Chat');
  const chip = el('span', { class: 'thread-chip' }, ctx.productName || 'Product');
  const flow = el('div', { class: 'msg-flow' });
  const progress = el('div', { class: 'progress', role: 'status', hidden: true },
    el('span', { class: 'progress-label' }, 'Bonnie is listening'),
    el('span', { class: 'pulse', 'aria-hidden': 'true' }),
    el('span', { class: 'pulse', 'aria-hidden': 'true' }),
    el('span', { class: 'pulse', 'aria-hidden': 'true' }));
  const typing = el('p', { class: 'typing', hidden: true });
  const scroller = el('div', { class: 'thread-scroll' }, flow, typing, progress);
  const status = el('div', { class: 'thread-status' }, skeleton(4));
  let messages = [];
  let noEarlier = false;
  let waiting = false;
  let seenConnect = false;
  const online = new Set();
  let typingTimer = null;
  let slowTimer = null;
  let giveUp = null;
  let poll = null;
  let quietPolls = 0;
  let membersOpen = !window.matchMedia('(max-width: 1023px)').matches;

  const actions = {
    onSave: (message, node) => exportMessage(message.id, 'md', node),
    onPdf: (message, node) => exportMessage(message.id, 'pdf', node),
    onCorrect: () => composer.prefill("That's not right — "),
    onOption: (row) => composer.prefill((row && (row.text || row.label)) || ''),
    onAsk: (text) => composer.prefill(text || ''),
  };

  const members = mountMembers({
    me: ctx.me,
    productId: ctx.productId,
    onHide: () => setMembersOpen(false),
    onChanged: () => document.dispatchEvent(new CustomEvent('bonnie:chats')),
  });
  const scrim = el('button', {
    type: 'button',
    class: 'members-scrim',
    'aria-label': 'Close members',
    hidden: true,
    onClick: () => setMembersOpen(false),
  });
  const memberBtn = el('button', {
    type: 'button',
    class: 'tool-btn',
    onClick: () => setMembersOpen(!membersOpen),
  }, 'Members');

  const composer = mountComposer({
    groupId,
    me: ctx.me,
    placeholder: `Ask about ${ctx.productName || 'this product'}, paste an image, or drop a file…`,
    onSend: (payload) => send(payload),
  });

  const root = el('section', { class: 'thread' },
    el('div', { class: 'thread-col' },
      el('header', { class: 'thread-head' },
        title,
        el('div', { class: 'thread-tools' },
          chip,
          button('Archive', { onClick: () => archive() }),
          memberBtn)),
      status,
      scroller,
      composer.node),
    scrim,
    members.node);

  function setMembersOpen(open) {
    membersOpen = open;
    applyMembers();
  }

  function applyMembers() {
    const narrow = window.matchMedia('(max-width: 1023px)').matches;
    members.node.classList.toggle('is-open', membersOpen && narrow);
    members.node.classList.toggle('is-shut', !membersOpen && !narrow);
    scrim.hidden = !(membersOpen && narrow);
    memberBtn.textContent = membersOpen && !narrow ? 'Hide members' : 'Members';
    memberBtn.setAttribute('aria-expanded', membersOpen ? 'true' : 'false');
  }

  function paint() {
    disposeBlockCharts(flow);
    clear(flow);
    flow.append(el('div', { class: 'day-div' },
      el('span', { class: 'spark', 'aria-hidden': 'true' }, '✦'),
      'Start of conversation'));
    if (!messages.length) {
      flow.append(el('p', { class: 'thread-empty' }, 'No messages yet. Ask Bonnie below.'));
    }
    for (const group of groupByDay(messages)) {
      flow.append(el('div', { class: 'day-div is-day' }, el('span', {}, group.day)));
      for (const message of group.messages) flow.append(renderMessage(message, ctx.me, actions));
    }
    if (messages.length >= 50 && !noEarlier) {
      flow.prepend(button('Earlier messages', { onClick: () => loadEarlier() }));
    }
    scroller.scrollTop = scroller.scrollHeight;
  }

  function addIncoming(message) {
    if (!message || message.group_id !== groupId || messages.some((item) => item.id === message.id)) return;
    messages.push(message);
    paint();
    if (waiting && isBonnie(message)) clearProgress();
    document.dispatchEvent(new CustomEvent('bonnie:chats'));
  }

  function showProgress(label) {
    const first = !waiting;
    waiting = true;
    progress.hidden = false;
    progress.querySelector('.progress-label').textContent = label || 'Bonnie is listening';
    if (first) scroller.scrollTop = scroller.scrollHeight;
    // Check for the reply while waiting, so a live update missed while this
    // chat was closed (or a dropped socket) cannot leave it waiting forever.
    if (!poll) poll = setInterval(() => { refresh(); }, 8000);
    if (!giveUp) {
      giveUp = setTimeout(() => {
        toast('Bonnie is taking longer than usual. The reply will show here when it arrives.');
      }, 120000);
    }
  }

  function clearProgress() {
    waiting = false;
    quietPolls = 0;
    progress.hidden = true;
    if (giveUp) clearTimeout(giveUp);
    giveUp = null;
    if (poll) clearInterval(poll);
    poll = null;
    composer.setBusy(false);
  }

  async function load() {
    slowTimer = setTimeout(() => {
      if (status.isConnected) status.append(el('p', { class: 'thread-empty' }, 'Still loading the thread…'));
    }, 8000);
    const [msgs, connectors] = await Promise.all([
      api(`/chat/${encodeURIComponent(groupId)}/messages`),
      ctx.productId ? api(`/connectors?product_id=${encodeURIComponent(ctx.productId)}`) : Promise.resolve(null),
    ]);
    if (slowTimer) clearTimeout(slowTimer);
    if (!root.isConnected) return;
    status.remove();
    if (!msgs || msgs.error) {
      scroller.prepend(errorPanel({
        title: 'Chat unavailable',
        message: msgs.error || 'The thread could not be loaded.',
        action: button('Try again', { onClick: () => window.location.reload() }),
      }));
      return;
    }
    messages = msgs.messages || [];
    // Opened while Bonnie is still writing a reply: show what she's doing.
    if (msgs.pending) showProgress(msgs.pending.label);
    if (title.textContent === 'Chat' && ctx.productId) {
      const list = await api(`/chat?product_id=${encodeURIComponent(ctx.productId)}`);
      const found = list && (list.groups || []).find((group) => group.id === groupId);
      if (found && found.name && root.isConnected) title.textContent = found.name;
    }
    const count = connectors && connectors.integrations ? connectors.integrations.length : null;
    if (ctx.productName && count != null) chip.textContent = `${ctx.productName} · ${count} connector${count === 1 ? '' : 's'}`;
    paint();
    members.load(groupId).then((loaded) => {
      if (loaded) composer.setMembers(loaded);
    });
  }

  async function loadEarlier() {
    const oldest = messages[0];
    if (!oldest) return;
    const res = await api(`/chat/${encodeURIComponent(groupId)}/messages?before=${encodeURIComponent(oldest.created_at)}`);
    if (!res || res.error) {
      toast((res && res.error) || 'Could not load earlier messages', 'error');
      return;
    }
    const older = res.messages || [];
    if (!older.length) {
      noEarlier = true;
      paint();
      return;
    }
    const ids = new Set(messages.map((item) => item.id));
    messages = older.filter((item) => !ids.has(item.id)).concat(messages);
    paint();
  }

  async function send(payload) {
    const draft = { text: payload.text, files: payload.files };
    composer.takeDraft();
    composer.setBusy(true);
    let attachments = [];
    try {
      if (draft.files.length) attachments = await uploadFiles(draft.files);
    } catch (err) {
      composer.restore(draft);
      composer.setBusy(false);
      toast((err && err.message) || 'Upload failed', 'error');
      return;
    }
    const res = await api(`/chat/${encodeURIComponent(groupId)}/messages`, {
      method: 'POST',
      body: {
        content: draft.text,
        attachments,
        model_id: payload.modelId === 'auto' ? undefined : payload.modelId,
      },
    });
    if (!root.isConnected) return;
    if (!res || res.error) {
      composer.restore(draft);
      composer.setBusy(false);
      toast((res && res.error) || 'Could not send', 'error');
      return;
    }
    if (res.message) addIncoming(res.message);
    composer.setBusy(false);
    showProgress('Bonnie is listening');
  }

  async function archive() {
    try {
      await archiveChat(groupId);
    } catch (err) {
      toast((err && err.message) || 'Could not archive this chat', 'error');
      return;
    }
    toast('Chat archived');
    document.dispatchEvent(new CustomEvent('bonnie:chats'));
    navigate(withProduct('/app/chat/new', ctx.productId));
  }

  function onSocket(packet) {
    if (!root.isConnected) return;
    const event = packet.event || '';
    const payload = packet.data || {};
    if (event === 'connected') {
      if (seenConnect) refresh();
      seenConnect = true;
      return;
    }
    if (event === 'agent:progress') {
      if (payload.group_id && payload.group_id !== groupId) return;
      if (waiting || payload.group_id === groupId) showProgress(payload.label || payload.step || 'Bonnie is working');
      return;
    }
    if (event === 'message:new' || event === 'agent:reply') {
      addIncoming(payload);
      return;
    }
    if (event === 'message:typing' && payload.group_id === groupId && payload.user_id !== ctx.me.id) {
      typing.hidden = false;
      typing.textContent = `${payload.user_name || 'Someone'} is typing…`;
      if (typingTimer) clearTimeout(typingTimer);
      typingTimer = setTimeout(() => { typing.hidden = true; }, 3000);
      return;
    }
    if (event === 'member:joined' || event === 'member:left') {
      if (payload.group_id === groupId) members.load(groupId);
      return;
    }
    if (event === 'user:online' || event === 'user:offline') {
      if (payload.user_id) {
        if (event === 'user:online') online.add(payload.user_id);
        else online.delete(payload.user_id);
      }
      members.setOnline(online);
      return;
    }
    if (event === 'group:update') {
      const id = (payload.group && payload.group.id) || payload.group_id;
      document.dispatchEvent(new CustomEvent('bonnie:chats'));
      if (id !== groupId) return;
      if (payload.type === 'archived') {
        navigate(withProduct('/app/chat/new', ctx.productId));
        return;
      }
      if (payload.group && payload.group.name) title.textContent = payload.group.name;
    }
  }

  async function refresh() {
    const before = new Set(messages.map((item) => item.id));
    const res = await api(`/chat/${encodeURIComponent(groupId)}/messages`);
    if (!res || res.error || !root.isConnected) return;
    messages = res.messages || [];
    paint();
    if (!waiting) return;
    if (messages.some((item) => isBonnie(item) && !before.has(item.id))) {
      clearProgress();
      return;
    }
    if (res.pending) {
      quietPolls = 0;
      showProgress(res.pending.label);
      return;
    }
    // No reply in progress on the server and none arrived: stop waiting
    // after two checks in a row (the send can take a moment to register).
    quietPolls += 1;
    if (quietPolls >= 2) clearProgress();
  }

  const stop = subscribe(onSocket);
  applyMembers();
  load();
  root.addEventListener('bonnie:unmount', () => {
    stop();
    if (slowTimer) clearTimeout(slowTimer);
    if (giveUp) clearTimeout(giveUp);
    if (poll) clearInterval(poll);
    if (typingTimer) clearTimeout(typingTimer);
  });
  window.addEventListener('resize', applyMembers);
  root.addEventListener('bonnie:unmount', () => window.removeEventListener('resize', applyMembers));
  return root;
}
